
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Plus } from "lucide-react";
import { BillingPeriod, updateBillingPeriod } from "@/hooks/useFinanceData";
import { BillingPeriodDialog } from "./BillingPeriodDialog";
import { toast } from "sonner";

interface BillingPeriodsCardProps {
  periods: BillingPeriod[];
  onRefetch: () => void;
}

export const BillingPeriodsCard = ({ periods, onRefetch }: BillingPeriodsCardProps) => {
  const handleActivatePeriod = async (period: BillingPeriod) => {
    try {
      await updateBillingPeriod(period.id, { status: 'active' });
      toast.success(`Período "${period.name}" activado`);
      onRefetch();
    } catch (error: any) {
      toast.error("Error al activar período: " + error.message);
    }
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Períodos de Facturación
          </CardTitle>
          <BillingPeriodDialog onPeriodCreated={onRefetch} />
        </div>
      </CardHeader>
      <CardContent>
        {periods.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              No hay períodos de facturación
            </p>
            <BillingPeriodDialog 
              onPeriodCreated={onRefetch}
              trigger={
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear tu primer período
                </Button>
              }
            />
          </div>
        ) : (
          <div className="space-y-3">
            {periods.slice(0, 5).map((period) => (
              <div key={period.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">{period.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(period.start_date).toLocaleDateString()} - {new Date(period.end_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={period.status === 'active' ? 'default' : period.status === 'closed' ? 'secondary' : 'outline'}
                  >
                    {period.status === 'active' ? 'Activo' : period.status === 'closed' ? 'Cerrado' : 'Borrador'}
                  </Badge>
                  {period.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleActivatePeriod(period)}
                    >
                      Activar
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {periods.length > 5 && (
              <p className="text-sm text-muted-foreground text-center">
                Y {periods.length - 5} más...
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
